import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { OptimizationService } from '../services/optimizationService';
import { OptimizationParameters, OptimizationResults, OptimizationResultsWithStats, ConvergenceData } from '../types';
import { saveResultsToHistory } from '../utils/historyStorage';
import { getOptimalParametersForTaskCount } from '../utils/parameterUtils';

export interface NotificationState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

interface OptimizationContextType {
  isRunning: boolean;
  results: OptimizationResultsWithStats | null;
  convergenceData: ConvergenceData | null;
  progress: number;
  currentAlgorithm: string;
  currentIteration: number;
  totalIterations: number;
  currentRun: number;
  totalRuns: number;
  notification: NotificationState | null;
  startOptimization: (params: OptimizationParameters, datasetText: string) => Promise<void>;
  startCompleteRun: (
    baseParams: OptimizationParameters,
    datasetText: string,
    workflowStart: number,
    workflowEnd: number,
    increment: number,
    onProgress?: (current: number, total: number, taskCount: number, params?: OptimizationParameters) => void
  ) => Promise<Array<{ taskCount: number; results: OptimizationResults; timestamp: string }>>;
  cancelOptimization: () => void;
  clearResults: () => void;
  showNotification: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
  hideNotification: () => void;
}

const OptimizationContext = createContext<OptimizationContextType | undefined>(undefined);

export const useOptimization = () => {
  const context = useContext(OptimizationContext);
  if (!context) {
    throw new Error('useOptimization must be used within OptimizationProvider');
  }
  return context;
};

interface OptimizationProviderProps {
  children: ReactNode;
}

export const OptimizationProvider: React.FC<OptimizationProviderProps> = ({ children }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<OptimizationResultsWithStats | null>(null);
  const [convergenceData, setConvergenceData] = useState<ConvergenceData | null>(null);
  const [progress, setProgress] = useState(0);
  const [currentAlgorithm, setCurrentAlgorithm] = useState('');
  const [currentIteration, setCurrentIteration] = useState(0);
  const [totalIterations, setTotalIterations] = useState(0);
  const [currentRun, setCurrentRun] = useState(0);
  const [totalRuns, setTotalRuns] = useState(1);
  const [notification, setNotification] = useState<NotificationState | null>(null);
  const [service] = useState(() => new OptimizationService());

  const showNotification = useCallback((message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setNotification({ open: true, message, severity });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification(null);
  }, []);

  const startOptimization = useCallback(async (
    params: OptimizationParameters,
    datasetText: string
  ) => {
    setIsRunning(true);
    setProgress(0);
    setResults(null);
    setConvergenceData(null);
    setCurrentAlgorithm('');
    setCurrentIteration(0);
    setTotalIterations(params.iterations);
    setCurrentRun(0);
    setTotalRuns(params.numberOfRuns || 1);

    const cancelFlag = { current: false };

    try {
      // Load dataset
      service.loadDataset(datasetText, params.taskCount);

      // Run optimization (with statistics if multiple runs)
      const numberOfRuns = params.numberOfRuns || 1;
      const useStatistics = numberOfRuns > 1;

      const result = useStatistics
        ? await service.runOptimizationWithStatistics(
            params,
            (algorithm) => {
              // Algorithm start callback
              if (!cancelFlag.current) {
                setCurrentAlgorithm(algorithm);
                setCurrentIteration(0);
                const algName = algorithm.split(' (Run')[0]; // Remove run suffix for matching
                const algorithmIndex = ['GA', 'Cultural', 'SFLA', 'SFLAGA', 'PSO', 'GAPSO'].indexOf(algName);
                if (algorithmIndex >= 0) {
                  const baseProgress = (algorithmIndex / 6) * 100;
                  setProgress(baseProgress);
                }
              }
            },
            (algorithm, generation, fitness) => {
              // Generation update callback
              if (!cancelFlag.current) {
                setCurrentIteration(generation);
                setCurrentAlgorithm(algorithm);
                const algorithmIndex = ['GA', 'Cultural', 'SFLA', 'SFLAGA', 'PSO', 'GAPSO'].indexOf(algorithm);
                if (algorithmIndex >= 0) {
                  const baseProgress = (algorithmIndex / 6) * 100;
                  const algorithmProgress = ((generation / params.iterations) * 100) / 6;
                  const totalProgress = Math.min(100, baseProgress + algorithmProgress);
                  setProgress(totalProgress);
                }
              }
            },
            (runNumber, totalRuns) => {
              if (!cancelFlag.current) {
                setCurrentRun(runNumber);
              }
            }
          )
        : await service.runOptimization(
            params,
            (algorithm) => {
              if (!cancelFlag.current) {
                setCurrentAlgorithm(algorithm);
                setCurrentIteration(0);
                const algorithmIndex = ['GA', 'Cultural', 'SFLA', 'SFLAGA', 'PSO', 'GAPSO'].indexOf(algorithm);
                const baseProgress = (algorithmIndex / 6) * 100;
                setProgress(baseProgress);
              }
            },
            (algorithm, generation, fitness) => {
              if (!cancelFlag.current) {
                setCurrentIteration(generation);
                setCurrentAlgorithm(algorithm);
                const algorithmIndex = ['GA', 'Cultural', 'SFLA', 'SFLAGA', 'PSO', 'GAPSO'].indexOf(algorithm);
                const baseProgress = (algorithmIndex / 6) * 100;
                const algorithmProgress = ((generation / params.iterations) * 100) / 6;
                const totalProgress = Math.min(100, baseProgress + algorithmProgress);
                setProgress(totalProgress);
              }
            }
          );

      if (!cancelFlag.current) {
        setResults(result.results);
        setConvergenceData(result.convergenceData);
        setProgress(100);
        setCurrentAlgorithm('Complete');
        setCurrentRun(0);
        
        // Save to history
        try {
          saveResultsToHistory(result.results);
        } catch (error) {
          console.warn('Failed to save results to history:', error);
        }
      }
    } catch (error) {
      console.error('Optimization error:', error);
      if (!cancelFlag.current) {
        showNotification(
          'Optimization failed: ' + (error instanceof Error ? error.message : String(error)),
          'error'
        );
      }
    } finally {
      if (!cancelFlag.current) {
        setIsRunning(false);
        setCurrentAlgorithm('');
        setCurrentRun(0);
      }
    }
  }, [service, showNotification]);

  const startCompleteRun = useCallback(async (
    baseParams: OptimizationParameters,
    datasetText: string,
    workflowStart: number,
    workflowEnd: number,
    increment: number,
    onProgress?: (current: number, total: number, taskCount: number, params?: OptimizationParameters) => void
  ): Promise<Array<{ taskCount: number; results: OptimizationResults; timestamp: string }>> => {
    setIsRunning(true);
    setProgress(0);
    setResults(null);
    setConvergenceData(null);
    setCurrentAlgorithm('Complete Run');
    setCurrentIteration(0);

    const cancelFlag = { current: false };
    const allResults: Array<{ taskCount: number; results: OptimizationResults; timestamp: string }> = [];
    
    // Calculate task counts
    const taskCounts: number[] = [];
    for (let taskCount = workflowStart; taskCount <= workflowEnd; taskCount += increment) {
      taskCounts.push(taskCount);
    }

    const totalRuns = taskCounts.length;
    setTotalRuns(totalRuns);
    setCurrentRun(0);

    try {
      for (let i = 0; i < taskCounts.length; i++) {
        if (cancelFlag.current) break;

        const taskCount = taskCounts[i];
        setCurrentRun(i + 1);
        setProgress((i / totalRuns) * 100);
        setCurrentAlgorithm(`Complete Run: ${taskCount} tasks (${i + 1}/${totalRuns})`);

        // Get optimal parameters for this task count
        const optimalParams = getOptimalParametersForTaskCount(taskCount);

        // Create parameters with optimal values for this task count
        const params: OptimizationParameters = {
          ...baseParams,
          taskCount,
          iterations: optimalParams.iterations,
          initialPopulation: optimalParams.initialPopulation,
          crossoverRate: optimalParams.crossoverRate,
          mutationRate: optimalParams.mutationRate,
          memeplexCount: optimalParams.memeplexCount,
        };

        // Notify progress callback with current parameters for UI update
        if (onProgress) {
          onProgress(i + 1, totalRuns, taskCount, params);
        }

        // Load dataset with new task count
        service.loadDataset(datasetText, taskCount);

        // Run optimization
        const result = await service.runOptimization(
          params,
          (algorithm) => {
            if (!cancelFlag.current) {
              setCurrentAlgorithm(`Complete Run: ${taskCount} tasks - ${algorithm}`);
            }
          },
          (algorithm, generation, fitness) => {
            if (!cancelFlag.current) {
              const runProgress = (i / totalRuns) * 100;
              const algorithmProgress = ((generation / params.iterations) * 100) / totalRuns;
              setProgress(Math.min(100, runProgress + algorithmProgress));
            }
          }
        );

        if (!cancelFlag.current) {
          const timestamp = new Date().toISOString();
          allResults.push({
            taskCount,
            results: result.results,
            timestamp,
          });

          // Update UI with current results and convergence data in real-time
          setResults({
            ...result.results,
            timestamp,
          });
          setConvergenceData(result.convergenceData);

          // Save to history
          try {
            saveResultsToHistory(result.results);
          } catch (error) {
            console.warn('Failed to save results to history:', error);
          }
        }
      }

      if (!cancelFlag.current) {
        setProgress(100);
        setCurrentAlgorithm('Complete Run Finished');
        setCurrentRun(0);
        // Results and convergenceData are already set after each iteration,
        // so the UI will show the last completed optimization
        // No need to set again here as it's already set in the loop
      }
    } catch (error) {
      console.error('Complete run error:', error);
      if (!cancelFlag.current) {
        showNotification(
          'Complete run failed: ' + (error instanceof Error ? error.message : String(error)),
          'error'
        );
      }
    } finally {
      if (!cancelFlag.current) {
        setIsRunning(false);
        setCurrentAlgorithm('');
        setCurrentRun(0);
      }
    }

    return allResults;
  }, [service, showNotification]);

  const cancelOptimization = useCallback(() => {
    // Note: Full cancellation requires chunk-based optimization
    // For now, this just stops UI updates
    setIsRunning(false);
    setProgress(0);
    setCurrentAlgorithm('Cancelled');
  }, []);

  const clearResults = useCallback(() => {
    setResults(null);
    setConvergenceData(null);
    setProgress(0);
  }, []);

  return (
    <OptimizationContext.Provider
      value={{
        isRunning,
        results,
        convergenceData,
        progress,
        currentAlgorithm,
        currentIteration,
        totalIterations,
        currentRun,
        totalRuns,
        notification,
        startOptimization,
        startCompleteRun,
        cancelOptimization,
        clearResults,
        showNotification,
        hideNotification,
      }}
    >
      {children}
    </OptimizationContext.Provider>
  );
};

